import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import { format, eachDayOfInterval, startOfMonth, endOfMonth } from 'date-fns';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const API_BASE_URL = "https://api.muktihospital.com";

const RevenueChart = () => {
    const [chartData, setChartData] = useState({ series: [], options: {} });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMonthlyRevenue = async () => {
            setLoading(true);
            const now = new Date();
            const firstDay = startOfMonth(now);
            const lastDay = endOfMonth(now);
            const daysOfMonth = eachDayOfInterval({ start: firstDay, end: lastDay });

            const labels = daysOfMonth.map(day => format(day, 'd'));
            const dailyTotals = Array(daysOfMonth.length).fill(0);

            const modules = [
                { url: "/api/appointment/fees" },
                { url: "/api/nicu/nicu-bills/bills" },
                { url: "/api/hospital/hospital-bills/bills" },
                { url: "/api/medicine-bill/bills" },
                { url: "/api/pathology-bill/bills" },
                { url: "/api/ambulance-call/calls" },
            ];

            try {
                const api = axios.create({ baseURL: API_BASE_URL, headers: { "x-api-key": API_KEY } });
                const promises = daysOfMonth.map((day, index) => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const dayPromises = modules.map(module =>
                        api.get(`${module.url}/range?startDate=${dateStr}&endDate=${dateStr}`)
                           .then(res => parseFloat(res.data.totalAmount || res.data.totalFee || 0))
                           .catch(() => 0)
                    );
                    return Promise.all(dayPromises).then(results => {
                        dailyTotals[index] = results.reduce((sum, val) => sum + val, 0);
                    });
                });
                await Promise.all(promises);
                
                // Calculate a running total for the line chart
                const runningTotal = dailyTotals.map((sum => value => sum += value)(0));

                setChartData({
                    series: [
                        { name: 'Daily Revenue', type: 'column', data: dailyTotals },
                        { name: 'Cumulative Revenue', type: 'line', data: runningTotal }
                    ],
                    options: {
                        chart: { height: 400, type: 'line', toolbar: { show: false } },
                        stroke: { width: [0, 5], dashArray: [0, 3] },
                        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
                        dataLabels: { enabled: false },
                        labels: labels,
                        yaxis: {
                            labels: {
                                align: "left",
                                formatter: (val) => `৳${(val / 1000).toFixed(0)}k`,
                                style: { colors: ["#9CA3AF"], fontSize: "12px" },
                            }
                        },
                        xaxis: { labels: { style: { colors: "#9CA3AF", fontSize: "12px" } } },
                        colors: ["#1E40AF", "#7C3AED"],
                        tooltip: { y: { formatter: (val) => `৳${Math.round(val).toLocaleString()}` } }
                    },
                });

            } catch (error) {
                console.error("Error fetching monthly revenue data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchMonthlyRevenue();
    }, []);

    if (loading) {
        return <Skeleton height={400} />;
    }

    return (
        <div>
            <ReactApexChart
                options={chartData.options}
                series={chartData.series}
                type="line"
                height={400}
            />
        </div>
    );
};

export default RevenueChart;